import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Typography, Table, Divider, Empty, Select, Progress, Tag, Tooltip, Spin, Alert, message } from 'antd';
import { CalendarOutlined, LineChartOutlined, AppstoreOutlined, UserOutlined, KeyOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { Line } from '@ant-design/plots';
import { ApiService } from '../services/api';

const { Paragraph, Text } = Typography;

interface UsageAnalysisProps {
  data: any;
  type: string;
}

interface ResourceUsageResponse {
  org_name: string;
  resource: string;
  usage_count: number;
}

interface EngagementScoreResponse {
  org_name: string;
  unique_days: number;
  days_since_last: number;
  unique_keywords: number;
  avg_daily_usage: number;
  total_api_usage: number;
  successful_requests: number;
  error_requests: number;
  error_rate: number;
  active_users: number;
  last_usage_date: string;
  usage_days_score: number;
  recency_score: number;
  keywords_score: number;
  daily_usage_score: number;
  total_score: number;
}

// 獲取分數對應的顏色
const getScoreColor = (score: number, max: number) => {
  const percentage = (score / max) * 100;
  if (percentage >= 80) return '#52c41a'; // 綠色
  if (percentage >= 60) return '#1890ff'; // 藍色
  if (percentage >= 40) return '#faad14'; // 黃色
  if (percentage >= 20) return '#fa8c16'; // 橙色
  return '#f5222d'; // 紅色
};

// 獲取總分對應的等級
const getScoreLevel = (score: number) => {
  if (score >= 60) return { text: '非常活躍', color: '#52c41a' };
  if (score >= 40) return { text: '活躍', color: '#1890ff' };
  if (score >= 20) return { text: '一般', color: '#faad14' };
  if (score >= 10) return { text: '較少活躍', color: '#fa8c16' };
  return { text: '不活躍', color: '#f5222d' };
};

const UsageAnalysis: React.FC<UsageAnalysisProps> = ({ data, type }) => {
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(undefined);
  const [orgList, setOrgList] = useState<Array<{org_id: string, org_name: string}>>([]);
  const [loadingOrgs, setLoadingOrgs] = useState<boolean>(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loadingChart, setLoadingChart] = useState<boolean>(false);
  // 區分「查詢失敗」與「真的無資料」：失敗要明確告知，不可靜默降級成空狀態
  const [orgListError, setOrgListError] = useState<boolean>(false);
  const [chartError, setChartError] = useState<boolean>(false);

  // 調試信息
  console.log('UsageAnalysis 組件渲染:', { type, orgListLength: orgList.length, selectedOrg, loadingOrgs });

  // 獲取組織列表
  useEffect(() => {
    const fetchOrgList = async () => {
      if (type === 'amount_daily') {
        setLoadingOrgs(true);
        setOrgListError(false);
        try {
          // 構建時間參數
          const timeParams: any = {};
          if (data.time_range?.start_date) {
            timeParams.start_date = data.time_range.start_date;
          }
          if (data.time_range?.end_date) {
            timeParams.end_date = data.time_range.end_date;
          }
          
          const response = await ApiService.getOrgList(timeParams);
          console.log('組織列表API響應:', response);
          
          // 檢查響應數據結構
          let orgArray = response;
          if (response && response.data && Array.isArray(response.data)) {
            orgArray = response.data;
          } else if (response && Array.isArray(response)) {
            orgArray = response;
          } else {
            console.error('Invalid org list response structure:', response);
            setOrgList([]);
            return;
          }
          
          console.log('提取的組織陣列:', orgArray);
          
          // 保存完整的組織信息（org_id 和 org_name）
          const orgData = orgArray
            .filter((item: any) => item.org_name && item.org_name.trim() !== '' && item.org_id)
            .map((item: any) => ({
              org_id: item.org_id,
              org_name: item.org_name
            }));
          
          console.log('處理後的組織列表:', orgData);
          
          setOrgList(orgData);
        } catch (error) {
          console.error('Failed to fetch org list:', error);
          setOrgList([]);
          setOrgListError(true);
          message.error('組織列表載入失敗，請稍後再試');
        } finally {
          setLoadingOrgs(false);
        }
      }
    };

    fetchOrgList();
  }, [type, data.time_range]);

  // 當選擇組織時，獲取該組織的數據
  useEffect(() => {
    const fetchAmountDaily = async () => {
      if (type === 'amount_daily' && selectedOrg) {
        setLoadingChart(true);
        setChartError(false);
        try {
          // 構建 API 參數對象
          const apiParams: any = {};
          if (data.time_range?.start_date) {
            apiParams.start_date = data.time_range.start_date;
          }
          if (data.time_range?.end_date) {
            apiParams.end_date = data.time_range.end_date;
          }
          apiParams.org_id = selectedOrg; // 使用 org_id 而不是 org_name

          console.log('調用 amount_daily API，參數:', apiParams);

          const response = await ApiService.getAmountDaily(apiParams);
          
          if (response && response.data && Array.isArray(response.data)) {
            setChartData(response.data);
          } else {
            console.error('Invalid amount_daily response:', response);
            setChartData([]);
          }
        } catch (error) {
          console.error('Failed to fetch amount_daily data:', error);
          setChartData([]);
          setChartError(true);
          message.error('每日搜尋數據載入失敗，請稍後再試');
        } finally {
          setLoadingChart(false);
        }
      } else {
        setChartData([]);
      }
    };

    fetchAmountDaily();
  }, [selectedOrg, type, data.time_range]);

  // 獲取選中組織的名稱（用於顯示）
  const getSelectedOrgName = () => {
    if (!selectedOrg) return null;
    const selectedOrgData = orgList.find(org => org.org_id === selectedOrg);
    return selectedOrgData ? selectedOrgData.org_name : null;
  };

  if (!data) {
    return <Empty description="無可用數據" />;
  }

  // 對於 amount_daily 類型，即使沒有 data.data 也要正常顯示
  if (!data.data && type !== 'amount_daily') {
    return <Empty description="無可用數據" />;
  }

  // 顯示時間範圍
  const renderTimeRange = () => {
    if (!data.time_range) return null;
    
    const { start_date, end_date } = data.time_range;
    return (
      <Text type="secondary">
        查詢時間範圍: {start_date} ~ {end_date || '今天'}
      </Text>
    );
  };

  // 資源使用情況
  const renderResourceUsage = () => {
    // 轉換API返回的數據格式為扁平結構
    const processedData: Array<{
      key: string;
      org_name: string;
      resource: string;
      usage_count: number;
    }> = [];
    
    // 檢查data.data是否為陣列
    if (data.data && Array.isArray(data.data)) {
      // 遍歷每個組織
      data.data.forEach((orgData: any, orgIndex: number) => {
        // 檢查組織資料是否符合預期格式 [orgName, { total, resources }]
        if (Array.isArray(orgData) && orgData.length === 2) {
          const orgName = orgData[0] || '未知組織';
          const details = orgData[1] as { total: number; resources: Record<string, number> } || {};
          
          // 如果有resources屬性，遍歷每個資源
          if (details && details.resources) {
            Object.entries(details.resources).forEach(([resource, count], resIndex) => {
              processedData.push({
                key: `${orgIndex}-${resIndex}`,
                org_name: orgName,
                resource: resource,
                usage_count: count || 0
              });
            });
          }
        }
      });
    }

    const columns: ColumnsType<ResourceUsageResponse> = [
      {
        title: '組織名稱',
        dataIndex: 'org_name',
        key: 'org_name',
        sorter: (a, b) => (a.org_name || '').localeCompare(b.org_name || ''),
        render: (value) => value || '未知組織',
      },
      {
        title: 'API資源路徑',
        dataIndex: 'resource',
        key: 'resource',
        sorter: (a, b) => (a.resource || '').localeCompare(b.resource || ''),
        render: (value) => value || '未知資源',
      },
      {
        title: '使用次數',
        dataIndex: 'usage_count',
        key: 'usage_count',
        sorter: (a, b) => (a.usage_count || 0) - (b.usage_count || 0),
        render: (value) => value || 0,
      },
    ];

    return (
      <>
        <Paragraph>此報表顯示各組織呼叫過哪些API資源及次數。</Paragraph>
        {renderTimeRange()}
        <Divider />
        <Table 
          dataSource={processedData} 
          columns={columns} 
          rowKey="key"
          pagination={{ pageSize: 10 }}
        />
      </>
    );
  };

  // 每日搜尋次數
  const renderAmountDaily = () => {
    // 處理從 API 獲取的圖表數據
    const processedData = chartData.map((item: any, index: number) => ({
      key: `daily-${index}`,
      org_name: item.org_name || '未知組織',
      request_date: item.request_date || '未知日期',
      search_count: item.search_count || 0,
      rolling_7day_avg: item.rolling_7day_avg || 0,
      rolling_30day_avg: item.rolling_30day_avg || 0
    }));

    // 準備圖表數據 - 分別準備兩個數據集
    const dailyData: Array<{
      date: string;
      value: number;
    }> = [];
    
    const averageData: Array<{
      date: string;
      value: number;
    }> = [];

    const weeklyAverageData: Array<{
      date: string;
      value: number;
    }> = [];

    // 將搜尋次數和移動平均數據分別處理
    processedData.forEach((item: any) => {
      const searchCount = parseFloat(item.search_count) || 0;
      const rolling7DayAvg = parseFloat(item.rolling_7day_avg) || 0;
      const rollingAvg = parseFloat(item.rolling_30day_avg) || 0;
      

      
      dailyData.push({
        date: item.request_date,
        value: searchCount,
      });

      weeklyAverageData.push({
        date: item.request_date,
        value: rolling7DayAvg,
      });
      
      averageData.push({
        date: item.request_date,
        value: rollingAvg,
      });
    });

    // 合併數據並添加類型標識
    const chartDataFormatted = [
      ...dailyData.map(item => ({ ...item, type: '每日搜尋次數' })),
      ...weeklyAverageData.map(item => ({ ...item, type: '7天移動平均' })),
      ...averageData.map(item => ({ ...item, type: '30天移動平均' }))
    ];

    // 按日期排序
    chartDataFormatted.sort((a, b) => a.date.localeCompare(b.date));
    


    // 圖表配置
    const config = {
      data: chartDataFormatted,
      xField: 'date',
      yField: 'value',
      colorField: 'type',
      color: ['#1890ff', '#52c41a', '#ff4d4f'],
      legend: {
        position: 'top' as const,
      },
              tooltip: {
          showTitle: true,
          title: 'date',
          showMarkers: true,
          shared: true,
          formatter: (datum: any) => {
            return {
              name: datum.type || '未知',
              value: String(datum.value || 0)
            };
          },
        },
      smooth: true,
      point: {
        size: 3,
      },
      lineStyle: {
        lineWidth: 2,
      },
    };

    const columns = [
      {
        title: '組織名稱',
        dataIndex: 'org_name',
        key: 'org_name',
        sorter: (a: any, b: any) => (a.org_name || '').localeCompare(b.org_name || ''),
      },
      {
        title: '日期',
        dataIndex: 'request_date',
        key: 'request_date',
        sorter: (a: any, b: any) => (a.request_date || '').localeCompare(b.request_date || ''),
        defaultSortOrder: 'descend' as const,
      },
      {
        title: '搜尋次數',
        dataIndex: 'search_count',
        key: 'search_count',
        sorter: (a: any, b: any) => (a.search_count || 0) - (b.search_count || 0),
        render: (value: any) => value || 0,
      },
      {
        title: '7天移動平均',
        dataIndex: 'rolling_7day_avg',
        key: 'rolling_7day_avg',
        render: (value: any) => (value || 0).toFixed(2),
        sorter: (a: any, b: any) => (a.rolling_7day_avg || 0) - (b.rolling_7day_avg || 0),
      },
      {
        title: '30天移動平均',
        dataIndex: 'rolling_30day_avg',
        key: 'rolling_30day_avg',
        render: (value: any) => (value || 0).toFixed(2),
        sorter: (a: any, b: any) => (a.rolling_30day_avg || 0) - (b.rolling_30day_avg || 0),
      },
    ];

    return (
      <>
        <Paragraph>此報表顯示各組織每日的搜尋次數統計。</Paragraph>
        {renderTimeRange()}
        <Divider />
        
        {/* 圖表區域 */}
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LineChartOutlined style={{ marginRight: 8 }} />
                每日搜尋次數趨勢圖
              </div>
              <Select
                placeholder="請選擇組織"
                style={{ width: 200 }}
                value={selectedOrg}
                onChange={setSelectedOrg}
                allowClear
                loading={loadingOrgs}
                options={orgList.map((org: {org_id: string, org_name: string}) => {
                  console.log('映射組織選項:', org);
                  return {
                    label: org.org_name,
                    value: org.org_id,
                  };
                })}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
          }
          style={{ marginBottom: 24 }}
        >
          {selectedOrg ? (
            loadingChart ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }} aria-busy="true">
                <Spin size="large" />
              </div>
            ) : chartError ? (
              <Alert
                type="error"
                showIcon
                message="數據載入失敗"
                description="每日搜尋數據查詢失敗，這不代表該組織沒有數據。請稍後再試或重新整理頁面。"
                style={{ margin: '40px 0' }}
              />
            ) : chartDataFormatted.length > 0 ? (
              <Line {...config} height={400} />
            ) : (
              <Empty
                description={`${getSelectedOrgName() || '該組織'}暫無數據`}
                style={{ padding: '60px 0' }}
              />
            )
          ) : orgListError ? (
            <Alert
              type="error"
              showIcon
              message="組織列表載入失敗"
              description="無法取得組織列表，請稍後再試或重新整理頁面。"
              style={{ margin: '40px 0' }}
            />
          ) : (
            <Empty
              description="請選擇組織以查看趨勢圖"
              style={{ padding: '60px 0' }}
            />
          )}
        </Card>

        {/* 表格區域 */}
        <Card title="詳細數據表格">
          <Table 
            dataSource={processedData} 
            columns={columns} 
            rowKey="key"
            pagination={{ pageSize: 10 }}
            sortDirections={['descend', 'ascend', 'descend']}
          />
        </Card>
      </>
    );
  };

  // 使用情況概覽
  const renderEngagementScore = () => {
    // 確保數據格式正確
    const processedData = data.data && Array.isArray(data.data) 
      ? data.data.map((item: any, index: number) => ({
          key: `engagement-${index}`,
          org_name: item.org_name || '未知組織',
          unique_days: item.unique_days || 0,
          days_since_last: item.days_since_last || 0,
          unique_keywords: item.unique_keywords || 0,
          avg_daily_usage: item.avg_daily_usage || 0,
          total_api_usage: item.total_api_usage || 0,
          successful_requests: item.successful_requests || 0,
          error_requests: item.error_requests || 0,
          error_rate: item.error_rate || 0,
          active_users: item.active_users || 0,
          last_usage_date: item.last_usage_date || '',
          usage_days_score: item.usage_days_score || 0,
          recency_score: item.recency_score || 0,
          keywords_score: item.keywords_score || 0,
          daily_usage_score: item.daily_usage_score || 0,
          total_score: item.total_score || 0
        }))
      : [];

    const columns: ColumnsType<EngagementScoreResponse> = [
      {
        title: '組織名稱',
        dataIndex: 'org_name',
        key: 'org_name',
        fixed: 'left',
        sorter: (a, b) => (a.org_name || '').localeCompare(b.org_name || ''),
      },
      {
        title: '使用天數',
        dataIndex: 'unique_days',
        key: 'unique_days',
        sorter: (a, b) => (a.unique_days || 0) - (b.unique_days || 0),
        render: (value) => `${value || 0} 天`,
      },
      {
        title: '最近使用天數',
        dataIndex: 'days_since_last',
        key: 'days_since_last',
        sorter: (a, b) => (a.days_since_last || 0) - (b.days_since_last || 0),
        render: (value) => `${value || 0} 天前`,
      },
      {
        title: '不同關鍵字數量',
        dataIndex: 'unique_keywords',
        key: 'unique_keywords',
        sorter: (a, b) => (a.unique_keywords || 0) - (b.unique_keywords || 0),
        render: (value) => value || 0,
      },
      {
        title: '平均每日使用次數',
        dataIndex: 'avg_daily_usage',
        key: 'avg_daily_usage',
        sorter: (a, b) => (a.avg_daily_usage || 0) - (b.avg_daily_usage || 0),
        render: (value) => (value || 0).toFixed(2),
      },
      {
        title: '活躍使用者',
        dataIndex: 'active_users',
        key: 'active_users',
        sorter: (a, b) => (a.active_users || 0) - (b.active_users || 0),
      },
      {
        title: '成功請求',
        dataIndex: 'successful_requests',
        key: 'successful_requests',
        sorter: (a, b) => (a.successful_requests || 0) - (b.successful_requests || 0),
      },
      {
        title: '錯誤率',
        dataIndex: 'error_rate',
        key: 'error_rate',
        sorter: (a, b) => (a.error_rate || 0) - (b.error_rate || 0),
        render: (value) => `${(value || 0).toFixed(2)}%`,
      },
      {
        title: '總API使用次數',
        dataIndex: 'total_api_usage',
        key: 'total_api_usage',
        sorter: (a, b) => (a.total_api_usage || 0) - (b.total_api_usage || 0),
        render: (value) => value || 0,
      },
      {
        title: '最近使用日期',
        dataIndex: 'last_usage_date',
        key: 'last_usage_date',
        sorter: (a, b) => (a.last_usage_date || '').localeCompare(b.last_usage_date || ''),
        render: (value) => value || '無',
      },
      {
        title: '使用天數分數',
        dataIndex: 'usage_days_score',
        key: 'usage_days_score',
        sorter: (a, b) => (a.usage_days_score || 0) - (b.usage_days_score || 0),
        render: (value) => {
          const safeValue = value || 0;
          return (
            <Tooltip title={`${safeValue}/30`}>
              <Progress 
                percent={Math.round((safeValue / 30) * 100)} 
                strokeColor={getScoreColor(safeValue, 30)}
                format={() => safeValue} 
                size="small" 
              />
            </Tooltip>
          );
        },
      },
      {
        title: '最近使用分數',
        dataIndex: 'recency_score',
        key: 'recency_score',
        sorter: (a, b) => (a.recency_score || 0) - (b.recency_score || 0),
        render: (value) => {
          const safeValue = value || 0;
          return (
            <Tooltip title={`${safeValue}/20`}>
              <Progress 
                percent={Math.round((safeValue / 20) * 100)} 
                strokeColor={getScoreColor(safeValue, 20)}
                format={() => safeValue} 
                size="small" 
              />
            </Tooltip>
          );
        },
      },
      {
        title: '關鍵字分數',
        dataIndex: 'keywords_score',
        key: 'keywords_score',
        sorter: (a, b) => (a.keywords_score || 0) - (b.keywords_score || 0),
        render: (value) => {
          const safeValue = value || 0;
          return (
            <Tooltip title={`${safeValue}/20`}>
              <Progress 
                percent={Math.round((safeValue / 20) * 100)} 
                strokeColor={getScoreColor(safeValue, 20)}
                format={() => safeValue} 
                size="small" 
              />
            </Tooltip>
          );
        },
      },
      {
        title: '每日使用分數',
        dataIndex: 'daily_usage_score',
        key: 'daily_usage_score',
        sorter: (a, b) => (a.daily_usage_score || 0) - (b.daily_usage_score || 0),
        render: (value) => {
          const safeValue = value || 0;
          return (
            <Tooltip title={`${safeValue}/10`}>
              <Progress 
                percent={Math.round((safeValue / 10) * 100)} 
                strokeColor={getScoreColor(safeValue, 10)}
                format={() => safeValue} 
                size="small" 
              />
            </Tooltip>
          );
        },
      },
      {
        title: '總分',
        dataIndex: 'total_score',
        key: 'total_score',
        fixed: 'right',
        sorter: (a, b) => (a.total_score || 0) - (b.total_score || 0),
        render: (value) => {
          const safeValue = value || 0;
          const { text, color } = getScoreLevel(safeValue);
          return (
            <Tooltip title={`${safeValue}/80 - ${text}`}>
              <Tag color={color} style={{ fontWeight: 'bold' }}>{safeValue}</Tag>
            </Tooltip>
          );
        },
      },
    ];

    // 計算平均分數
    const calculateAverages = () => {
      if (!processedData || processedData.length === 0) return null;
      
      const totalOrgs = processedData.length;
      const sums = processedData.reduce((acc: any, curr: EngagementScoreResponse) => {
        return {
          unique_days: acc.unique_days + (curr.unique_days || 0),
          days_since_last: acc.days_since_last + (curr.days_since_last || 0),
          unique_keywords: acc.unique_keywords + (curr.unique_keywords || 0),
          avg_daily_usage: acc.avg_daily_usage + (curr.avg_daily_usage || 0),
          total_api_usage: acc.total_api_usage + (curr.total_api_usage || 0),
          successful_requests: acc.successful_requests + (curr.successful_requests || 0),
          error_requests: acc.error_requests + (curr.error_requests || 0),
          active_users: acc.active_users + (curr.active_users || 0),
          usage_days_score: acc.usage_days_score + (curr.usage_days_score || 0),
          recency_score: acc.recency_score + (curr.recency_score || 0),
          keywords_score: acc.keywords_score + (curr.keywords_score || 0),
          daily_usage_score: acc.daily_usage_score + (curr.daily_usage_score || 0),
          total_score: acc.total_score + (curr.total_score || 0),
        };
      }, {
        unique_days: 0,
        days_since_last: 0,
        unique_keywords: 0,
        avg_daily_usage: 0,
        total_api_usage: 0,
        successful_requests: 0,
        error_requests: 0,
        active_users: 0,
        usage_days_score: 0,
        recency_score: 0,
        keywords_score: 0,
        daily_usage_score: 0,
        total_score: 0,
      });

      return {
        unique_days: Math.round(sums.unique_days / totalOrgs),
        days_since_last: Math.round(sums.days_since_last / totalOrgs),
        unique_keywords: Math.round(sums.unique_keywords / totalOrgs),
        avg_daily_usage: sums.avg_daily_usage / totalOrgs,
        total_api_usage: sums.total_api_usage / totalOrgs,
        successful_requests: sums.successful_requests,
        error_requests: sums.error_requests,
        active_users: sums.active_users,
        overall_error_rate: sums.total_api_usage
          ? (sums.error_requests / sums.total_api_usage) * 100
          : 0,
        usage_days_score: Math.round(sums.usage_days_score / totalOrgs),
        recency_score: Math.round(sums.recency_score / totalOrgs),
        keywords_score: Math.round(sums.keywords_score / totalOrgs),
        daily_usage_score: Math.round(sums.daily_usage_score / totalOrgs),
        total_score: Math.round(sums.total_score / totalOrgs),
      };
    };

    const averages = calculateAverages();

    return (
      <>
        <Paragraph>
          此報表只使用 AnalyticsFastapiQuery，顯示各組織的活躍使用者、成功請求、錯誤率、使用天數與關鍵字等指標。
          總分滿分為 80 分，由四個分項組成：使用天數(30分)、最近使用(20分)、關鍵字多樣性(20分)和每日使用頻率(10分)。
        </Paragraph>
        {renderTimeRange()}
        
        {averages && (
          <>
            <Divider>Analytics 使用摘要</Divider>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="組織數"
                    value={processedData.length}
                    prefix={<AppstoreOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="活躍使用者（組織加總）"
                    value={averages.active_users}
                    prefix={<UserOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="成功請求"
                    value={averages.successful_requests}
                    prefix={<KeyOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic 
                    title="整體錯誤率"
                    value={averages.overall_error_rate}
                    precision={2}
                    suffix="%"
                    prefix={<CalendarOutlined />}
                    valueStyle={{ color: averages.overall_error_rate > 5 ? '#cf1322' : '#3f8600' }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
        
        <Divider>詳細使用情況與參與度參考分數</Divider>
        <Table 
          dataSource={processedData} 
          columns={columns} 
          rowKey="key"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1500 }}
        />
      </>
    );
  };

  // 根據類型渲染不同的內容
  const renderContent = () => {
    switch(type) {
      case 'resource_usage':
        return renderResourceUsage();
      case 'amount_daily':
        return renderAmountDaily();
      case 'engagement_score':
        return renderEngagementScore();
      default:
        return <Empty description="未知的分析類型" />;
    }
  };

  return (
    <Card>
      {renderContent()}
    </Card>
  );
};

export default UsageAnalysis;
